/**
 * NotificationSettings - Panel konfiguracji powiadomień email
 * Tylko dla administratorów
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Chip,
  Paper,
  Stack
} from '@mui/material';
import {
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import api from '../services/apiWithAuth';

const NotificationSettings = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Stan ustawień
  const [settings, setSettings] = useState({
    notification_enabled: false,
    recipient_email: '',
    notification_time: '08:00',
    updated_by: 'admin'
  });
  
  // Stan schedulera
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Załaduj ustawienia przy otwarciu
  useEffect(() => {
    if (open) {
      loadSettings();
      loadSchedulerStatus();
    }
  }, [open]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/notification-settings/settings');
      setSettings({
        notification_enabled: response.data.notification_enabled,
        recipient_email: response.data.recipient_email,
        notification_time: response.data.notification_time,
        updated_by: 'admin'
      });
    } catch (err) {
      // Jeśli ustawienia nie istnieją - użyj domyślnych
      if (err.response?.status === 404) {
        console.log('No settings found, using defaults');
      } else {
        setError('Błąd ładowania ustawień: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await api.get('/notification-settings/status');
      setSchedulerStatus(response.data);
    } catch (err) {
      console.error('Failed to load scheduler status:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      await api.put('/notification-settings/settings', settings);
      setSuccess('Ustawienia zostały zapisane pomyślnie');
      
      // Odśwież status schedulera
      await loadSchedulerStatus();
      
      // Zamknij dialog po 2 sekundach
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError('Błąd zapisywania ustawień: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await api.post('/notification-settings/send-test-email');
      
      if (response.data.success) {
        setSuccess('✅ Email testowy wysłany pomyślnie na: ' + response.data.recipient);
      } else {
        setError('❌ Nie udało się wysłać emaila: ' + response.data.message);
      }
    } catch (err) {
      setError('Błąd wysyłania testu: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTesting(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = field === 'notification_enabled' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatNextRunTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Ustawienia Powiadomień Email</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Alert messages */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* Scheduler Status */}
            {schedulerStatus && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Status Schedulera
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {schedulerStatus.scheduler_running ? (
                    <>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Chip label="Aktywny" color="success" size="small" />
                    </>
                  ) : (
                    <>
                      <ErrorIcon color="error" fontSize="small" />
                      <Chip label="Zatrzymany" color="error" size="small" />
                    </>
                  )}
                </Box>
                {schedulerStatus.next_run_time && (
                  <Typography variant="body2" color="textSecondary">
                    <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Następne sprawdzenie: {formatNextRunTime(schedulerStatus.next_run_time)}
                  </Typography>
                )}
              </Paper>
            )}

            <Divider />

            {/* Włącz/Wyłącz */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.notification_enabled}
                  onChange={handleChange('notification_enabled')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Włącz powiadomienia</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Automatyczne powiadomienia o zmianach w serwisach
                  </Typography>
                </Box>
              }
            />

            {/* Email odbiorcy */}
            <TextField
              fullWidth
              label="Email odbiorcy"
              type="email"
              value={settings.recipient_email}
              onChange={handleChange('recipient_email')}
              placeholder="admin@example.com"
              helperText="Adres email, na który będą wysyłane powiadomienia"
              InputProps={{
                startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
              }}
              required
            />

            {/* Godzina wysyłki */}
            <TextField
              fullWidth
              label="Godzina sprawdzenia"
              type="time"
              value={settings.notification_time}
              onChange={handleChange('notification_time')}
              helperText="Codziennie o tej godzinie system sprawdza zmiany w serwisach"
              InputProps={{
                startAdornment: <ScheduleIcon color="action" sx={{ mr: 1 }} />
              }}
              inputProps={{
                step: 300 // 5 min
              }}
            />

            {/* Informacja */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Jak to działa?</strong><br />
                System codziennie o wybranej godzinie sprawdza czy jutro zmienia się osoba na serwisie. 
                Jeśli tak - wysyła email z informacją o nowym przypisaniu.
              </Typography>
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving || testing}>
          Anuluj
        </Button>
        
        <Button
          onClick={handleTest}
          disabled={!settings.recipient_email || saving || testing}
          startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
          variant="outlined"
        >
          Test
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={!settings.recipient_email || saving || testing}
          startIcon={saving ? <CircularProgress size={20} /> : null}
          variant="contained"
          color="primary"
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationSettings;
